// frontend/src/components/WorkspaceFiscalYearSelector.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Building2, Calendar, ChevronDown, Plus } from 'lucide-react';
import { Icon } from './ui';

const WorkspaceFiscalYearSelector = () => {
  const navigate = useNavigate();
  const {
    workspaces,
    activeWorkspace,
    fiscalYears,
    currentFiscalYear,
    switchWorkspace,
    switchFiscalYear,
    loading: workspaceLoading,
    tenantType,
  } = useWorkspace();

  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showYearMenu, setShowYearMenu] = useState(false);

  const handleWorkspaceChange = (workspaceId) => {
    switchWorkspace(workspaceId);
    setShowWorkspaceMenu(false);
  };

  const handleFiscalYearChange = (fiscalYearId) => {
    switchFiscalYear(fiscalYearId);
    setShowYearMenu(false);
  };

  return (
    <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/40 rounded-lg p-3">
      {/* Sélecteur Workspace */}
      <div className="relative mb-3">
        <label className="block text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-2">
          📁 Dossier Comptable
        </label>

        {workspaceLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Icon name="IconSpinner" className="animate-spin" />
            <span>Chargement...</span>
          </div>
        ) : tenantType === 'company' ? (
          <div className="flex items-center gap-2 text-sm text-white bg-gray-700 px-3 py-2 rounded-md border border-gray-600">
            <Building2 className="w-4 h-4 text-blue-400" />
            <span className="font-semibold">{activeWorkspace?.name || 'Mon Entreprise'}</span>
          </div>
        ) : workspaces?.length === 0 ? (
          <div className="text-xs text-yellow-400">
            <Icon name="IconAlertTriangle" className="mr-1" />
            Aucun dossier disponible
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-700 text-white text-sm border border-gray-600 rounded-md hover:bg-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="font-medium">
                  {activeWorkspace?.name || 'Sélectionner...'}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {showWorkspaceMenu && (
              <div className="absolute top-full left-0 mt-2 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-50">
                <div className="p-2 border-b border-gray-700">
                  <p className="text-xs font-medium text-gray-400 uppercase px-2">
                    Workspaces
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {workspaces?.map((ws) => (
                    <button
                      key={ws._id}
                      onClick={() => handleWorkspaceChange(ws._id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors ${
                        activeWorkspace?._id === ws._id
                          ? 'bg-blue-600 text-white font-medium'
                          : 'text-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{ws.name}</span>
                        {ws.type === 'CABINET_OWN' && (
                          <span className="text-xs bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded">
                            Cabinet
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeWorkspace && (
              <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-400">
                <span
                  className={`px-2 py-0.5 rounded ${
                    activeWorkspace.type === 'CABINET_OWN'
                      ? 'bg-orange-900/40 text-orange-300'
                      : 'bg-green-900/40 text-green-300'
                  }`}
                >
                  {activeWorkspace.type === 'CABINET_OWN' ? 'Cabinet' : 'Client'}
                </span>
                {activeWorkspace.clientId?.taxId && (
                  <span className="font-mono">MF: {activeWorkspace.clientId.taxId}</span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Séparateur - toujours affiché */}
      <div className="h-px bg-blue-700/30 mb-3" />

      {/* Sélecteur Exercice - TOUJOURS AFFICHÉ */}
      <div className="relative">
        <label className="block text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-2">
          📅 Exercice Comptable
        </label>

        {!activeWorkspace ? (
          <div className="text-xs text-gray-400 bg-gray-700/50 px-3 py-2 rounded-md border border-gray-600">
            <Icon name="IconInfoCircle" className="mr-1" />
            Sélectionnez d'abord un dossier
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowYearMenu(!showYearMenu)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-700 text-white text-sm border border-gray-600 rounded-md hover:bg-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="font-medium">
                  {currentFiscalYear?.label || currentFiscalYear?.name || 'Aucun exercice'}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {showYearMenu && (
              <div className="absolute top-full left-0 mt-2 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-2xl z-50">
                <div className="p-2 border-b border-gray-700">
                  <p className="text-xs font-medium text-gray-400 uppercase px-2">
                    Exercices comptables
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {!fiscalYears || fiscalYears.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400 text-center">
                      Aucun exercice
                    </div>
                  ) : (
                    fiscalYears.map((year) => (
                      <button
                        key={year._id}
                        onClick={() => handleFiscalYearChange(year._id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors ${
                          currentFiscalYear?._id === year._id
                            ? 'bg-blue-600 text-white font-medium'
                            : 'text-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{year.label || year.name}</span>
                          <div className="flex items-center gap-2">
                            {year.isActive && (
                              <span className="text-xs bg-green-900/40 text-green-300 px-2 py-0.5 rounded">
                                Actif
                              </span>
                            )}
                            {year.status === 'CLOSED' && (
                              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                                Clôturé
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <div className="p-2 border-t border-gray-700">
                  <button
                    onClick={() => {
                      setShowYearMenu(false);
                      navigate('/fiscal-years');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-400 hover:bg-blue-600/20 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Nouvel exercice</span>
                  </button>
                </div>
              </div>
            )}

            {currentFiscalYear && (
              <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-400">
                <Icon name="IconCalendar" className="text-blue-400" />
                <span>
                  {currentFiscalYear.startDate} → {currentFiscalYear.endDate}
                </span>
                {currentFiscalYear.status === 'CLOSED' && (
                  <span className="px-2 py-0.5 rounded bg-red-900/40 text-red-300">
                    Clôturé
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceFiscalYearSelector;